import React from 'react';
import { Box, Button, Typography, Stepper, Step, StepLabel, Divider } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import CardView from '../components/CardView';
import SubstepHeader from '../components/SubstepHeader';
import KittyInfo from '../components/steps/KittyInfo';
import Goals from '../components/steps/Goals';
import Preferences from '../components/steps/Preferences';
import useRecommendationsForm from '../hooks/useRecommendationsForm';
import { STEPS } from '../constants/steps';
import '../style/Quiz.css';

const Recommendations = () => {
  const navigate = useNavigate();
  const {
    step,
    kittySubstep,
    goalsSubstep,
    preferencesSubstep,
    formData,
    loading,
    recommendations,
    submitted,
    ageError,
    foodTypeError,

    getCurrentSubsteps,
    resetQuiz,
    handleChange,
    handleFoodTypeToggle,
    handleBack,
    handleNext,
    getNextButtonText,
    setSubmitted
  } = useRecommendationsForm({ initialStep: 1 });

  const handleQuizBack = () => {
    if (step === 1 && kittySubstep === 0) {
      navigate('/Recommendations');
      return;
    }
    handleBack();
  };

  // Render results after submission
  if (submitted) {
    return (
      <div className="recommendations-container">
        <div className="page-header">
          <h1 className="page-title">Recommendations</h1>
        </div>
        <div className="recommendations-form">
          <div className="recommendations-results">           

            <Typography variant="h6" gutterBottom align="center">
              I think you might like these recommendations
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" align="center" color="text.secondary">
                Ranked from #1 (best match) to #10
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{ color: 'text.secondary', mt: 2 }}
              >
                Recommendations are based solely on user‑provided preferences; we do not rate or endorse product quality.
              </Typography>
            </Box>
            {/* Duplicate Start New Quiz button at top */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={() => {
                resetQuiz();
                // Scroll to top after reset
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  const recommendationsContainer = document.querySelector('.recommendations-container');
                  if (recommendationsContainer) {
                    recommendationsContainer.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }, 200);
              }}>
                Start new quiz
              </Button>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => {
                  setSubmitted(false);
                  setTimeout(() => {
                    handleNext();
                  }, 100);
                }}
                disabled={loading}
              >
                {loading ? 'Regenerating...' : 'Regenerate Recommendations'}
              </Button>
            </Box>

            <div className="data-grid-container">
              <Box sx={{ width: '100%' }}>
                <CardView cards={[...recommendations].sort((a, b) => a.rank - b.rank)} />
              </Box>
            </div>

            {/* Nutrient value disclaimers */}
            <Box sx={{ 
              mt: 3, 
              mb: 3, 
              mx: 1.5,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 1.2 
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'center'
                }}
              >
                <span style={{ color: 'blue' }}>*</span>
                Nutrient values displayed in blue indicate that the data come from guaranteed analysis and is not fully accurate
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'center'
                }}
              >
                <span style={{ color: 'purple' }}>**</span>
                Nutrient values displayed in purple were obtained through AI-powered search
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  mt: 1
                }}
              >
                As an Amazon Associate, The Picky Kitty earns from qualifying purchases.{" "}
                <a 
                  href="/amazon-disclosure" 
                  style={{ 
                    color: 'inherit', 
                    textDecoration: 'underline',
                    fontWeight: '500'
                  }}
                >
                  Learn more
                </a>
              </Typography>
            </Box>

            {/* Original Start New Quiz button at bottom */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={() => {
                resetQuiz();
                // Scroll to top after reset
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  const recommendationsContainer = document.querySelector('.recommendations-container');
                  if (recommendationsContainer) {
                    recommendationsContainer.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }, 200);
              }}>
                Start new quiz
              </Button>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => {
                  setSubmitted(false);
                  setTimeout(() => {
                    handleNext();
                  }, 100);
                }}
                disabled={loading}
              >
                {loading ? 'Regenerating...' : 'Regenerate Recommendations'}
              </Button>
            </Box>
          </div>
        </div>
      </div >
    );
  }

  // Render current step based on step state
  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return (
          <KittyInfo
            formData={formData}
            ageError={ageError}
            kittySubstep={kittySubstep}
            onFormChange={handleChange}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <Goals
            formData={formData}
            foodTypeError={foodTypeError}
            goalsSubstep={goalsSubstep}
            onFormChange={handleChange}
            onFoodTypeToggle={handleFoodTypeToggle}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <Preferences
            formData={formData}
            loading={loading}
            preferencesSubstep={preferencesSubstep}
            onFormChange={handleChange}
            onNext={handleNext}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  // Determine if Next button should be disabled
  const isNextButtonDisabled = () => {
    // Step 1: Kitty Info - Age is mandatory
    if (step === 1 && kittySubstep === 0 && !formData.age) {
      return true;
    }
    
    // Step 2: Goals - Food type is mandatory
    if (step === 2 && goalsSubstep === 0 && formData.foodTypes.length === 0) {
      return true;
    }
    
    // Step 3: Preferences - All substeps are optional, so no mandatory validation needed
    // But we can add specific validation if needed in the future
    
    return false;
  };

  // Render quiz form
  return (
    <div className="recommendations-container">
      <div className="page-header">
        <h1 className="page-title">Recommendations</h1>
      </div>
      <div className="recommendations-form">
        {step > 0 && (
          <div className="stepper-container">
            <Stepper activeStep={step - 1} alternativeLabel>
              {STEPS.slice(1).map((label, index) => (
                <Step key={index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Divider sx={{ my: 2 }} />
            <SubstepHeader {...getCurrentSubsteps()} />
            <Divider sx={{ my: 2 }} />
          </div>
        )}

        {renderCurrentStep()}

        {step > 0 && (
          <div className="form-buttons">
            {!(step === 1 && kittySubstep === 0) && (
              <Button variant="outlined" onClick={handleQuizBack}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextButtonDisabled()}
              sx={{
                backgroundColor: isNextButtonDisabled() ? '#bdbdbd' : '#1976d2',
                color: isNextButtonDisabled() ? '#9e9e9e' : '#ffffff',
                borderColor: isNextButtonDisabled() ? '#bdbdbd' : '#1976d2',
                '&:hover': {
                  backgroundColor: isNextButtonDisabled() ? '#bdbdbd' : '#1565c0',
                  borderColor: isNextButtonDisabled() ? '#bdbdbd' : '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#bdbdbd',
                  color: '#9e9e9e',
                  borderColor: '#bdbdbd',
                  cursor: 'not-allowed',
                }
              }}
            >
              {getNextButtonText()}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommendations;